import { useEffect, useState } from 'react';
import AuthenticatedLayout from '../layouts/AuthenticatedLayout';
import SecondaryButton from "../components/SecondaryButton";
import AddBiznisi from "./Biznisi/Add";
import PrimaryButton from "../components/PrimaryButton";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PrimaryOutlineButton from "../components/PrimaryOutlineButton";
import { Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';


export default function Biznisi() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [biznisi, setBiznisi] = useState([]);
    const [listing, setListing] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12); // default for grid
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState('list');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [searchQuery, setSearchQuery] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // Reset itemsPerPage when view mode changes
    useEffect(() => {
        if (viewMode === 'grid') setItemsPerPage(12);
        else setItemsPerPage(100);
        setCurrentPage(1);
    }, [viewMode]);

    const filteredBiznisi = biznisi.filter((biz) =>
        biz.naziv_brenda?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        biz.lokacija?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        biz.listing_id?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    let displayedBiznisi = filteredBiznisi;
    if (sortKey) {
        displayedBiznisi = [...filteredBiznisi].sort((a, b) => {
            let aValue = a[sortKey] ?? '';
            let bValue = b[sortKey] ?? '';
            // Numeric sort for view_count and cijena
            if (sortKey === 'view_count' || sortKey === 'cijena') {
                aValue = Number(aValue) || 0;
                bValue = Number(bValue) || 0;
            }
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }

    const totalPages = Math.ceil(displayedBiznisi.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = displayedBiznisi.slice(indexOfFirstItem, indexOfLastItem);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleAction = (action) => setAction(action);

    const edit = (listing) => {
        setListing(listing);
        handleAction('add');
    };

    const add = () => {
        setListing(null);
        handleAction('add');
    };

    useEffect(() => {
        const userData = localStorage.getItem('wp_user');
        if (userData) setUser(JSON.parse(userData));
        getList();
    }, []);

    const getList = () => {
        const token = localStorage.getItem('wp_jwt');
        if (!token) return;
        const apiUrl = process.env.REACT_APP_WORDPRESS_URL;
        fetch(`${apiUrl}/wp-json/biznis/v1/biznisi`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data?.data?.status == 403) {
                    localStorage.removeItem('wp_jwt');
                    localStorage.removeItem('wp_user');
                    Swal.fire({
                        html: 'Session Expired',
                        icon: 'error',
                        showConfirmButton: false,
                        timer: 3000,
                        position: "bottom-end",
                    });
                    setTimeout(() => navigate('/login'), 2000);
                    throw new Error('Session expired');
                } else {
                    setBiznisi(data);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching biznisi:', err);
                setLoading(false);
            });
    };

    const handleCancel = (refresh = false) => {
        handleAction('list');
        if (refresh) getList();
    };

    const exportListings = () => {
        if (!biznisi || biznisi.length === 0) return;
        const headers = ["ID BROJ", "Naziv Branda", "Lokacija", "Cijena", "Pregledi", "Faza Prodaje", "Sazetak"];
        const rows = biznisi.map(item => [
            item.listing_id,
            item.naziv_brenda,
            item.lokacija,
            item.cijena,
            item.view_count ?? 0,
            item.faza_prodaje,
            item.sazetak?.replace(/(\r\n|\n|\r)/gm, " ").replace(/"/g, '""')
        ]);
        const csvContent = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell}"`).join(","))].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "biznisi_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <AuthenticatedLayout
            user={user}
            header={
                <div className="flex justify-between items-center">
                    <h2 className="font-semibold text-xl text-white leading-tight">Biznisi</h2>
                    {action === 'list' && (
                        <div className="flex gap-2">
                            <SecondaryButton onClick={add}>Dodaj novi Biznis</SecondaryButton>
                            <PrimaryOutlineButton onClick={exportListings}>Izvoz CSV</PrimaryOutlineButton>
                        </div>
                    )}
                    {action === 'add' && (
                        <SecondaryButton onClick={() => handleAction('list')}>Otkazati</SecondaryButton>
                    )}
                </div>
            }
        >
            <div className="sm:mt-4 mx-auto sm:px-6 lg:px-8">
                <div className="bg-white shadow-sm sm:rounded-lg p-3 sm:p-8">
                    {action === 'list' && (
                        <>
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">

                                <input
                                    type="text"
                                    placeholder="Pretraži biznise..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-1/2"
                                />
                                <div
                                    className="flex flex-col sm:flex-row sm:items-center justify-end mb-4 gap-2 sm:w-2/3">
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="border border-gray-300 rounded px-3 rounded-lg py-2 text-sm"
                                    >
                                        {(viewMode === 'grid' ? [8, 12, 16, 20] : [100, 500, 1000]).map(n => (
                                            <option key={n} value={n}>{n} po stranici</option>
                                        ))}
                                    </select>

                                    <div className="flex gap-2 items-center">
                                        <button
                                            onClick={() => setViewMode('grid')}
                                            className={`p-2 rounded-lg border border-gray-300 ${viewMode === 'grid' ? 'bg-blue-950 text-white' : 'bg-gray-200 text-gray-600'}`}
                                            title="Grid View"
                                        >
                                            <Squares2X2Icon className="w-5 h-5"/>
                                        </button>
                                        <button
                                            onClick={() => setViewMode('list')}
                                            className={`p-2 rounded-lg border border-gray-300 ${viewMode === 'list' ? 'bg-blue-950 text-white' : 'bg-gray-200 text-gray-600'}`}
                                            title="List View"
                                        >
                                            <ListBulletIcon className="w-5 h-5"/>
                                        </button>
                                    </div>

                                </div>
                            </div>

                            {loading ? (
                                <div className="flex flex-col w-full items-center">
                                    <div
                                        className="animate-spin border-2 border-t-2 border-blue-950 w-5 h-5 rounded-full"></div>
                                </div>
                            ) : (
                                <>
                                    {currentItems.length === 0 ? (
                                        <p className="text-gray-500">Nema pronađenih tvrtki.</p>
                                    ) : (
                                        viewMode === 'grid' ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                                {currentItems.map((biznis) => (
                                                    <div
                                                        key={biznis.id}
                                                        onClick={() => edit(biznis)}
                                                        className="group relative cursor-pointer bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg hover:shadow-xl hover:bg-gray-200"
                                                    >
                                                        {/* SOLD Label */}
                                                        {biznis.faza_prodaje?.toLowerCase() === 'prodano' && (
                                                            <div className="absolute top-12 -left-12 rotate-[-45deg] bg-red-600 text-white text-xs font-bold px-20 py-1 shadow-lg border-white border z-20">
                                                                PRODANO
                                                            </div>
                                                        )}

                                                        {/* Image */}
                                                        {biznis.featured_image ? (
                                                            <img
                                                                src={biznis.featured_image}
                                                                alt={biznis.naziv_brenda}
                                                                className="w-full h-48 object-cover transform transition-transform duration-300 group-hover:scale-105"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-48 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                                                                <span className="text-gray-400 text-sm">Nema istaknute slike</span>
                                                            </div>
                                                        )}

                                                        {/* Content */}
                                                        <div className="p-4">
                                                            <h3 className="text-lg font-semibold text-gray-800">
                                                                {biznis.naziv_brenda.length > 29 ? `${biznis.naziv_brenda.substring(0, 29)}...` : biznis.naziv_brenda}
                                                            </h3>
                                                            <p className="text-sm text-gray-600 mt-2">
                                                                {biznis.sazetak.length > 75 ? `${biznis.sazetak.substring(0, 75)}...` : biznis.sazetak}
                                                            </p>
                                                            <div className="flex items-center justify-between mt-2">
                                                                <p className="text-sm text-gray-500">{biznis.lokacija}</p>
                                                                <div className="flex items-center gap-1 bg-blue-100 group-hover:bg-blue-200 text-blue-950 text-xs font-semibold px-2 py-1 rounded-full">
                                                                    👁 <span>{biznis.view_count ?? 0}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                            </div>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                                                    <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('listing_id')}>
                                                            ID BROJ {sortKey === 'listing_id' && (sortOrder === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('naziv_brenda')}>
                                                            Naziv Branda {sortKey === 'naziv_brenda' && (sortOrder === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('lokacija')}>
                                                            Lokacija {sortKey === 'lokacija' && (sortOrder === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('cijena')}>
                                                            Cijena {sortKey === 'cijena' && (sortOrder === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('faza_prodaje')}>
                                                            Faza Prodaje {sortKey === 'faza_prodaje' && (sortOrder === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer" onClick={() => handleSort('view_count')}>
                                                            Pregledi {sortKey === 'view_count' && (sortOrder === 'asc' ? '▲' : '▼')}
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                    {currentItems.map((biznis, index) => (
                                                        <tr
                                                            key={biznis.id}
                                                            className="hover:bg-gray-200 cursor-pointer"
                                                            onClick={() => edit(biznis)}
                                                        >
                                                            <td className="px-4 py-2 text-sm text-gray-700">{biznis.listing_id}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-800 font-medium">{biznis.naziv_brenda}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-600">{biznis.lokacija}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-600">{biznis.cijena}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-600">{biznis.faza_prodaje}</td>
                                                            <td className="px-4 py-2 text-sm text-gray-600">{biznis.view_count ?? 0}</td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )
                                    )}


                                    {filteredBiznisi.length !== 0 && (
                                        <div className="flex justify-between items-center mt-6">
                                            <PrimaryButton
                                                onClick={handlePrevPage}
                                                disabled={currentPage === 1}
                                                className="px-4 py-2"
                                            >
                                                Prev
                                            </PrimaryButton>
                                            <span className="mx-4 text-sm">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <PrimaryButton
                                                onClick={handleNextPage}
                                                disabled={currentPage === totalPages}
                                                className="px-4 py-2"
                                            >
                                                Next
                                            </PrimaryButton>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {action === 'add' && <AddBiznisi business={listing} onCancel={handleCancel} />}
                </div>
            </div>
        </AuthenticatedLayout>
    );
}
